import React, { useMemo, useRef, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/transaction/components/StockTransactionsToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { RootState } from '@/setup/redux/RootReducer';

import {
  createDefaultStockTransactionForm,
  InventoryStockTransactionFormData,
  InventoryStockTransactionRow,
  stockTransactionStateOptions,
} from '../types';
import StockTransactionForm from './StockTransactionForm';
import StockTransactionList, { StockTransactionListHandle } from './StockTransactionList';

const buildFormMetadata = (formData: InventoryStockTransactionFormData, transactionId?: string) => ({
  formData,
  transactionId,
});

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveCompanyIdForQuery = (companyId?: string) => {
  if (!companyId || companyId === 'all' || companyId === 'global' || !UUID_PATTERN.test(companyId)) {
    return '';
  }
  return companyId;
};

export default function StockTransactionModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const companies = useSelector((state: RootState) => state.auth.companies);
  const listRef = useRef<StockTransactionListHandle>(null);

  const companyOptions = useMemo(
    () =>
      (companies || []).map((company) => ({
        company_id: company.company_id,
        company_name: company.company_name,
      })),
    [companies]
  );

  const [snackbarMessage, setSnackbarMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState({ company: 'all', stockState: 'all' });
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCompanyId = useMemo(
    () => String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim(),
    [selectedCompany?.company_id],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const listScopeCompanyId = useMemo(
    () => resolveCompanyIdForQuery(activeCompanyFilter),
    [activeCompanyFilter],
  );

  const {
    canCreate: canCreateStockTransaction,
    resolvePermissionsForCompanyId,
    canReadRecord,
  } = useModuleToolbarPermissions({
    resource: 'inventory.stocktransaction',
    activeCompanyFilter,
    updateTabTypes: ['update-stock-transaction'],
  });

  const defaultTabs: TabData[] = useMemo(() => {
    return [
      { id: 0, title: 'Giao dịch kho', type: 'list', closable: false, createdAt: Date.now() },
    ];
  }, []);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_stock_transaction_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateStockTransaction),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-stock-transaction', 'update-stock-transaction'],
    allowMultipleTabTypes: ['new-stock-transaction', 'update-stock-transaction'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId = listScopeCompanyId || selectedCompanyId;
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `GD kho #${currentTabs.filter((tab) => tab.type === 'new-stock-transaction').length + 1}`,
        type: 'new-stock-transaction',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata(createDefaultStockTransactionForm(baseCompanyId)),
        },
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryStockTransactionFormData | undefined;
  const activeTransactionId = activeTabData?.metadata?.transactionId as string | undefined;

  const handleToolbarFilterChange = (field: string, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const updateFormData = (updates: Partial<InventoryStockTransactionFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;
    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeTransactionId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
  };

  const openExistingStockTransaction = (row: InventoryStockTransactionRow) => {
    if (!canReadRecord(row)) {
      setSnackbarMessage({ message: 'Bạn không có quyền xem chi tiết giao dịch kho.', severity: 'error' });
      return;
    }
    guardAndRun(() => {
      openTab({
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: row.reference_no || row.item_code || 'Giao dịch kho',
        type: 'update-stock-transaction',
        closable: true,
        entityId: row.id,
        metadata: {
          companyId: row.company_id,
          ...buildFormMetadata(
          {
            company_id: row.company_id,
            company_name: row.company_name,
            store_code: row.store_code,
            store_name: row.store_name,
            item_code: row.item_code,
            item_name: row.item_name,
            txn_type: row.txn_type,
            quantity_delta: String(row.quantity_delta),
            unit_price: String(row.unit_price),
            line_amount: String(row.line_amount),
            posted_at: row.posted_at,
            document_type: row.document_type,
            document_id: String(row.document_id || ''),
            reference_no: row.reference_no,
            lot_no: row.lot_no,
            bin_location: row.bin_location,
            updated_at: row.updated_at || '',
            note: row.note || '',
          },
          row.id
        ),
        },
      });
    });
  };

  const handleSave = () => {
    if (!activeFormData || !activeTabData?.id) return;

    const isUpdateMode = Boolean(activeTransactionId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage({ message: 'Bạn không có quyền cập nhật giao dịch kho.', severity: 'error' });
      return;
    }
    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage({ message: 'Bạn không có quyền tạo giao dịch kho.', severity: 'error' });
      return;
    }

    handleUnsavedChangesUpdate(false, activeTabData.id);
    setSnackbarMessage({
      message: 'Giao dịch kho được sinh tự động từ chứng từ đã ghi sổ, không chỉnh sửa trực tiếp.',
      severity: 'error',
    });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
  };

  const handleRefresh = () => {
    listRef.current?.requestRefresh();
    setSnackbarMessage({ message: 'Đã refresh giao dịch kho.', severity: 'success' });
  };

  const formCompanyOptions = useMemo(() => {
    if (!activeFormData?.company_id) {
      return companyOptions;
    }
    const exists = companyOptions.some((company) => company.company_id === activeFormData.company_id);
    if (exists) {
      return companyOptions;
    }
    return [
      {
        company_id: activeFormData.company_id,
        company_name: activeFormData.company_name || activeFormData.company_id,
      },
      ...companyOptions,
    ];
  }, [activeFormData?.company_id, activeFormData?.company_name, companyOptions]);

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryStockTransactionFormData | undefined;

              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((current) => !current)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              stockState: filters.stockState,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            stockStateOptions={stockTransactionStateOptions}
                            onRefresh={handleRefresh}
                          />
                        }
                        dataTable={
                          <StockTransactionList
                            ref={listRef}
                            companyId={listScopeCompanyId || selectedCompanyId}
                            searchQuery={searchQuery}
                            stockState={filters.stockState}
                            onSelectRow={openExistingStockTransaction}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <StockTransactionForm
                        formData={tabFormData}
                        companyDisabled
                        companyOptions={formCompanyOptions}
                        onChangeForm={(updates) => {
                          if (tab.id !== activeTabData?.id) {
                            updateTab(tab.id, {
                              metadata: buildFormMetadata(
                                { ...tabFormData, ...updates },
                                tab.metadata?.transactionId as string | undefined
                              ),
                            });
                            handleUnsavedChangesUpdate(true, tab.id);
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onSave={handleSave}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert severity={snackbarMessage?.severity || 'success'} onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
